import { startAuthentication } from '@simplewebauthn/browser'

import { api } from '../api.js'
import { prfAnfordern, prfAuslesen } from './prf.js'

/**
 * Holt vom Passkey das Geheimnis, mit dem sich der Schlüsselspeicher öffnen
 * lässt — ohne neue Anmeldung, mit bestehender Sitzung.
 *
 * Der Rückgabewert ist `null`, wenn die Zeremonie geklappt hat, der
 * Authenticator aber kein PRF liefert. Das ist kein Fehler, sondern eine
 * Eigenschaft des Geräts: Dann bleibt der Satz der Weg.
 *
 * ## Warum der Server überhaupt gefragt wird
 *
 * Das Geheimnis kommt aus dem Authenticator und verlässt den Browser nie — für
 * das Entsperren allein bräuchte es keinen Server. Die Challenge von dort macht
 * die Zeremonie aber überhaupt erst prüfbar: Ohne sie liefe der Zähler gegen
 * geklonte Authenticator nie mit, und im Protokoll stünde nicht, wann jemand
 * den Schlüsselspeicher geöffnet hat.
 */
export async function passkeyGeheimnis(): Promise<ArrayBuffer | null> {
  const options = await api.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>(
    '/auth/webauthn/unlock/options',
  )
  const response = await startAuthentication({
    optionsJSON: { ...options, extensions: prfAnfordern() },
  })
  await api.post('/auth/webauthn/unlock/verify', { response })
  return prfAuslesen(response.clientExtensionResults)
}

/** Kann dieser Browser überhaupt Passkeys? Alte Geräte können es nicht. */
export function passkeysMoeglich(): boolean {
  return typeof PublicKeyCredential !== 'undefined'
}

/**
 * Auf diesem Gerät liefert der Authenticator kein PRF — nicht wieder anbieten.
 *
 * Steht hier und nicht mehr in `Entsperren.tsx`, seit die App den Passkey beim
 * Start von selbst versucht: Zwei Stellen fragen danach, und zwei Kopien
 * desselben localStorage-Schlüssels wären genau die Sorte Fehler, die erst
 * auffällt, wenn eine davon vergessen wird.
 */
const KEIN_PRF = 'fh-prf-nicht-moeglich'

export function prfAussichtslos(): boolean {
  try { return localStorage.getItem(KEIN_PRF) === '1' } catch { return false }
}

export function prfAussichtslosMerken(): void {
  try { localStorage.setItem(KEIN_PRF, '1') } catch { /* Privatmodus: dann eben nicht */ }
}
